const PrivacyPolicyForCandidates = () => (
  <div className="flex flex-col gap-2 text-sm font-inter">
    <h2 className="mb-2">Privacy Policy for Candidates</h2>
    <div className="px-0 md:px-6">
      <p>
        <strong>Effective Date:</strong> May 2, 2025
      </p>
      <p className="mt-4">
        At Qureos Inc. ("Qureos," "we," "our," or "us"), we understand how
        important your privacy is. This Privacy Policy explains how we collect,
        use, store, and protect your personal information when you use our
        platform as a job seeker. Our goal is to make your experience as
        seamless, transparent, and secure as possible, while ensuring we comply
        with the strictest global privacy standards.
      </p>
      <p className="mt-4">
        By using our platform, you consent to the practices outlined in this
        policy.
      </p>
    </div>

    <h3 className="mt-8">1. Definitions</h3>
    <div className="px-0 md:px-6">
      <p>
        To ensure clarity throughout this policy, we define key terms below:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>"Candidate"</strong>: Refers to any individual using Qureos to
          explore career opportunities, apply for jobs, or receive career
          coaching.
        </li>
        <li className="mb-4">
          <strong>"Platform"</strong>: Refers to the Qureos website
          (Qureos.com), mobile applications, and related digital services.
        </li>
        <li className="mb-4">
          <strong>"Personal Data"</strong>: Means any information that can
          identify you, such as your name, email, voice recordings, or job
          history.
        </li>
        <li className="mb-4">
          <strong>"Processing"</strong>: Includes any operation performed on
          personal data, such as collection, recording, storage, use, or
          sharing.
        </li>
        <li className="mb-4">
          <strong>"Services"</strong>: Refers to all features provided to
          Candidates, including job matching, career guidance, and employer
          connections.
        </li>
      </ul>
      <p>
        By defining these terms clearly, we aim to avoid ambiguity and support
        your understanding of our practices.
      </p>
    </div>

    <h3 className="mt-8">2. Scope</h3>
    <div className="px-0 md:px-6">
      <p>
        This Privacy Policy applies exclusively to Candidates who use Qureos to
        access job-related services. It explains how we handle your personal
        data, whether you're actively searching for a role, completing a
        profile, or passively exploring opportunities.
      </p>
      <p className="mt-4">
        Our policy does not apply to Companies using Qureos to hire talent.
        Those users are covered under a separate privacy policy specific to
        companies.
      </p>
      <p className="mt-4">
        We are committed to ensuring that all personal data processed under this
        policy is handled fairly, lawfully, and transparently.
      </p>
    </div>

    <h3 className="mt-8">3. Information We Collect</h3>
    <div className="px-0 md:px-6">
      <p>
        We collect various categories of personal information to provide and
        improve our services. This includes data you provide directly, data we
        collect automatically, and data from third-party sources.
      </p>

      <h4 className="mt-4">3.1 Personal Information You Provide</h4>
      <p>When you use our Platform, you may submit:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Full Name</strong>: Used to personalize your experience and
          identify you in our system.
        </li>
        <li className="mb-4">
          <strong>Email Address</strong>: Required for login, notifications, and
          employer communication.
        </li>
        <li className="mb-4">
          <strong>Phone Number</strong>: Enables verification and support.
        </li>
        <li className="mb-4">
          <strong>Address (City, Country)</strong>: Helps filter jobs based on
          location preferences.
        </li>
        <li className="mb-4">
          <strong>Nationality and Gender</strong>: Optional fields that may be
          used to help meet employer diversity goals or local employment
          regulations.
        </li>
        <li className="mb-4">
          <strong>CV/Resume and Work History</strong>: Forms the basis of your
          professional profile and is shared with employers.
        </li>
        <li className="mb-4">
          <strong>Job Preferences</strong>: Enables more accurate matching with
          relevant job opportunities.
        </li>
        <li className="mb-4">
          <strong>Audio and Video Recordings</strong>: Collected during
          assessments; used solely for skills evaluation and not for identity
          verification.
        </li>
      </ul>
      <p>
        We treat all these details as private and secure, ensuring they are only
        processed for the stated purposes.
      </p>

      <h4 className="mt-4">3.2 Automatically Collected Data</h4>
      <p>We also collect usage data through cookies and device logs:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Device Information</strong>: Type of browser, device model, OS
          version.
        </li>
        <li className="mb-4">
          <strong>Activity Logs</strong>: Pages visited, time spent, click
          behavior.
        </li>
      </ul>
      <p>
        This data helps us improve our Platform and ensure it works reliably
        across different systems.
      </p>

      <h4 className="mt-4">3.3 Data from Public Sources</h4>
      <p>
        Where permitted by law, we may enrich your Candidate profile using
        publicly available data, such as from LinkedIn or GitHub. This is used
        to enhance your discoverability and match potential job opportunities
        more accurately.
      </p>
    </div>

    <h3 className="mt-8">4. How We Collect Information</h3>
    <div className="px-0 md:px-6">
      <p>
        We gather data from multiple sources to ensure completeness and
        accuracy:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Direct Input</strong>: Information you submit when registering
          or updating your profile.
        </li>
        <li className="mb-4">
          <strong>Platform Interactions</strong>: Data generated through use of
          features like job applications, video responses, or saved searches.
        </li>
        <li className="mb-4">
          <strong>Public Enrichment</strong>: Select data collected from public
          web sources to strengthen your professional presence.
        </li>
      </ul>
      <p>
        This multi-source approach supports more effective matchmaking between
        Candidates and Companies.
      </p>
    </div>

    <h3 className="mt-8">5. How We Use Your Information</h3>
    <div className="px-0 md:px-6">
      <p>
        We use your personal data to provide, personalize, and optimize the
        Qureos experience:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Account Creation and Profile Management</strong>: Establish
          and maintain your Candidate account.
        </li>
        <li className="mb-4">
          <strong>Job Matching and Recommendations</strong>: Connect you with
          roles aligned to your background and preferences.
        </li>
        <li className="mb-4">
          <strong>Employer Introductions</strong>: Share your profile with
          relevant companies.
        </li>
        <li className="mb-4">
          <strong>Interview and Assessment Facilitation</strong>: Enable you to
          complete audio/video responses for evaluation.
        </li>
        <li className="mb-4">
          <strong>Notifications</strong>: Inform you about job openings,
          updates, or feature enhancements.
        </li>
        <li className="mb-4">
          <strong>Platform Improvements</strong>: Analyze user trends to improve
          functionality and navigation.
        </li>
      </ul>
      <p>
        We are careful to limit use to only those purposes necessary to deliver
        core services.
      </p>
    </div>

    <h3 className="mt-8">6. Legal Basis for Processing</h3>
    <div className="px-0 md:px-6">
      <p>
        We rely on one or more of the following legal bases under GDPR and
        similar frameworks:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Consent</strong>: For collecting optional data and sending
          non-essential communications.
        </li>
        <li className="mb-4">
          <strong>Contractual Necessity</strong>: To deliver services you
          requested, such as account creation and job applications.
        </li>
        <li className="mb-4">
          <strong>Legitimate Interests</strong>: For fraud prevention, system
          security, and platform analytics.
        </li>
        <li className="mb-4">
          <strong>Legal Obligations</strong>: When required to retain or
          disclose information under applicable law.
        </li>
      </ul>
      <p>
        We ensure that all data processing is proportionate and consistent with
        your rights.
      </p>
    </div>

    <h3 className="mt-8">7. How We Share Your Information</h3>
    <div className="px-0 md:px-6">
      <p>We only share personal data in limited cases:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>With Employers</strong>: When you apply to a job or match with
          a role, your profile is shared with the employer.
        </li>
        <li className="mb-4">
          <strong>With Service Providers</strong>: We use cloud hosting,
          analytics tools, and assessment platforms that may temporarily access
          your data under strict contracts.
        </li>
        <li className="mb-4">
          <strong>With Authorities</strong>: Where required by law or legal
          process.
        </li>
      </ul>
      <p>
        We do not sell, lease, or monetize your personal information under any
        circumstances.
      </p>
    </div>

    <h3 className="mt-8">8. International Data Transfers</h3>
    <div className="px-0 md:px-6">
      <p>
        To support regional compliance and performance, your data is stored
        based on your location:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>EU Users</strong>: Data stored in secure data centers within
          the European Union.
        </li>
        <li className="mb-4">
          <strong>UAE Users</strong>: Data hosted within the UAE.
        </li>
        <li className="mb-4">
          <strong>Saudi Users</strong>: Data stored in accordance with KSA's
          data sovereignty requirements.
        </li>
      </ul>
      <p>
        Cross-border transfers are only conducted with adequate protections like
        encryption and approved legal safeguards.
      </p>
    </div>

    <h3 className="mt-8">9. Data Security</h3>
    <div className="px-0 md:px-6">
      <p>We maintain high standards of security to keep your data safe:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Encryption</strong>: All sensitive information is encrypted
          both at rest and in transit.
        </li>
        <li className="mb-4">
          <strong>Access Control</strong>: Only authorized personnel with
          defined roles can access personal data.
        </li>
        <li className="mb-4">
          <strong>Monitoring and Alerts</strong>: Continuous monitoring to
          detect suspicious activity.
        </li>
        <li className="mb-4">
          <strong>Incident Response</strong>: In case of a breach, we act
          swiftly and transparently.
        </li>
      </ul>
      <p>
        Your security is integral to our promise of responsible data
        stewardship.
      </p>
    </div>

    <h3 className="mt-8">10. Your Rights</h3>
    <div className="px-0 md:px-6">
      <p>You have several important rights under GDPR and equivalent laws:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Access</strong>: You can request a copy of the personal data
          we hold.
        </li>
        <li className="mb-4">
          <strong>Correction</strong>: You may ask to update or correct
          inaccuracies.
        </li>
        <li className="mb-4">
          <strong>Deletion</strong>: You can delete your profile or request
          erasure of data.
        </li>
        <li className="mb-4">
          <strong>Restriction</strong>: You may limit how we process your data.
        </li>
        <li className="mb-4">
          <strong>Portability</strong>: You can receive your data in a
          machine-readable format.
        </li>
        <li className="mb-4">
          <strong>Objection</strong>: You can object to processing for certain
          purposes, such as marketing.
        </li>
        <li className="mb-4">
          <strong>Withdrawal of Consent</strong>: Where consent is the legal
          basis, you can withdraw it at any time.
        </li>
      </ul>
      <p>
        You can exercise these rights through your account settings or by
        contacting hello@qureos.com.
      </p>
    </div>

    <h3 className="mt-8">11. Data Retention</h3>
    <div className="px-0 md:px-6">
      <p>
        We retain your data only as long as it serves the purposes outlined in
        this policy:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Active Users</strong>: Your account remains active as long as
          you log in at least once per year.
        </li>
        <li className="mb-4">
          <strong>Inactive Users</strong>: If your account is inactive for 12
          months, you will receive three warning notifications before we delete
          your data.
        </li>
        <li className="mb-4">
          <strong>Account Deletion</strong>: Upon request, your data is removed
          within 30 days unless required for legal reasons.
        </li>
      </ul>
      <p>We never retain personal data longer than necessary.</p>
    </div>

    <h3 className="mt-8">12. Changes to This Policy</h3>
    <div className="px-0 md:px-6">
      <p>
        We may revise this Privacy Policy from time to time to reflect
        improvements, legal updates, or changes in our data practices. If we
        make material changes, we will notify you via email or a notice on the
        Platform before they take effect.
      </p>
      <p>
        You are encouraged to review this Policy regularly to stay informed.
      </p>
    </div>

    <h3 className="mt-8">13. Contact Us</h3>
    <div className="px-0 md:px-6">
      <p>
        If you have questions or concerns about this Privacy Policy or how we
        process your personal information, please contact us:
      </p>
      <p>
        <strong>Qureos Inc.</strong>
      </p>
      <ul className="list-disc pl-5">
        <li>
          Email:{' '}
          <a href="mailto:hello@qureos.com" className="text-qureosPrimary">
            hello@qureos.com
          </a>
        </li>
        <li>Jurisdiction: UAE and KSA</li>
      </ul>
    </div>
  </div>
)

export default PrivacyPolicyForCandidates
